import ConditionalToolTip from "components/design/conditionalToolTip";
import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import Modal from "components/DesignSystem/Modal/Modal";
import { AddAsset } from "components/FixedAsset/AddAsset";
import { OptionDropdown } from "components/icons/optionDropdown";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { LockedUi } from "components/LockedUi/LockedUi";
import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";
import { useDeleteFixedAssetMutation } from "store/apis/fixedAsset";
import {
  DepreciationSchedulePreview,
  FixedAssetResult,
} from "types/Models/fixedAsset";
import { BackendError } from "types/utils/error";
import ThreeDots from "static/images/ThreeDots.svg";
import { stopPropagation } from "utils/stopPropagation";
import { TriangleInfo } from "components/icons/TriangleInfo";
import { StatusModal } from "components/DesignSystem/StatusModal/StatusModal";
import { Padlock } from "components/icons/Padlock";

export const FixedAssetAction = ({
  fixedAsset,
  onDeleteSuccess,
}: {
  fixedAsset: FixedAssetResult;
  onDeleteSuccess?: (deletedId: string) => void;
}) => {
  const deleteModal = useModal();
  const editModal = useModal();
  const { alertToast, successToast } = useToast();
  const { uuid: fixedAssetId, edit_delete_allowed } = fixedAsset;
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();
  const [deleteFixedAsset, { isLoading }] = useDeleteFixedAssetMutation();

  const onDelete = async () => {
    try {
      await deleteFixedAsset({
        groupId,
        fixedAssetIds: [fixedAssetId],
        entityId,
      }).unwrap();
      deleteModal.close();
      onDeleteSuccess?.(fixedAssetId);
      successToast({ message: "Asset deleted successfully" });
    } catch (error) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error as {}
      );
    }
  };

  const scheduleData: DepreciationSchedulePreview = {
    fixed_asset: {
      ...fixedAsset,
      asset_account_id: fixedAsset.asset_account.uuid,
      accumulated_depreciation_account_id:
        fixedAsset.accumulated_depreciation_account.uuid,
      depreciation_account_id: fixedAsset.depreciation_account.uuid,
    },
    depreciation_schedule: fixedAsset.depreciation_schedule,
  };

  return (
    <LockedUi
      feature={WRITE_PERMISSION_KEYS.ACCOUNTING}
      blockedUi={
        <Button size="small" customType="ghost_icon" disabled>
          <Padlock />
        </Button>
      }
    >
      <span onClick={stopPropagation}>
        <Dropdown.Root>
          <Dropdown.Trigger asChild>
            <div>
              <Button size="xsmall" customType="ghost_icon">
                <img src={ThreeDots} alt="Action" className="t-select-none" />
              </Button>
            </div>
          </Dropdown.Trigger>
          <Dropdown.Portal>
            <Dropdown.Content align="end" className="!t-min-w-40">
              <Dropdown.Item
                type="ordinary"
                onSelect={() => {
                  if (edit_delete_allowed) {
                    editModal.open();
                  }
                }}
                disabled={!edit_delete_allowed}
              >
                <ConditionalToolTip
                  condition={
                    !edit_delete_allowed && (
                      <div>
                        {" "}
                        <div className="t-text-white t-text-subtitle-sm t-flex t-justify-start t-mb-1">
                          Editing Blocked
                        </div>
                        <p className="t-text-neutral-30 t-text-body-sm t-mb-0">
                          Asset has entries in locked financial period
                        </p>
                      </div>
                    )
                  }
                >
                  <span>Edit</span>
                </ConditionalToolTip>
              </Dropdown.Item>
              <Dropdown.Item
                type="danger"
                onSelect={() => {
                  if (edit_delete_allowed) {
                    deleteModal.open();
                  }
                }}
                disabled={!edit_delete_allowed}
              >
                <ConditionalToolTip
                  condition={
                    !edit_delete_allowed && (
                      <div>
                        {" "}
                        <div className="t-text-white t-text-subtitle-sm t-flex t-justify-start t-mb-1">
                          Deleting Blocked
                        </div>
                        <p className="t-text-neutral-30 t-text-body-sm t-mb-0">
                          Asset has entries in locked financial period
                        </p>
                      </div>
                    )
                  }
                  className="t-w-full"
                >
                  <span>Delete</span>
                </ConditionalToolTip>
              </Dropdown.Item>
            </Dropdown.Content>
          </Dropdown.Portal>
        </Dropdown.Root>

        <Modal.Root open={deleteModal.isOpen} onOpenChange={deleteModal.close}>
          <Modal.Content>
            <StatusModal type="warning" title="Delete asset?">
              <div className="t-text-body-sm t-text-text-30 t-mb-2">
                Are you sure you want to delete fixed asset '
                {fixedAsset.asset_name}' ?
              </div>
              <div className="t-text-body-sm-italic t-italic t-text-text-30">
                {(scheduleData?.depreciation_schedule ?? []).length} associated
                journal entries will also be deleted and it will affect other
                financial reports.
              </div>
            </StatusModal>
            <Modal.FooterButtonGroup>
              <Modal.RawClose asChild disabled={isLoading}>
                <Button disabled={isLoading}>Cancel</Button>
              </Modal.RawClose>
              <Button
                customType="primary"
                onClick={onDelete}
                isLoading={isLoading}
                disabled={isLoading}
              >
                Delete
              </Button>
            </Modal.FooterButtonGroup>
          </Modal.Content>
        </Modal.Root>
        {editModal.isOpen && (
          <AddAsset
            isOpen={editModal.isOpen}
            close={editModal.close}
            depreciationScheduleData={scheduleData}
            fixedAssetId={fixedAssetId}
          />
        )}
      </span>
    </LockedUi>
  );
};
